import { useState } from "react";
import { useGame } from "@/hooks/useGame";
import { useTranslation } from "@/i18n/useTranslation.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Users, AlertCircle, Table } from "lucide-react";
import logo from "../../assets/images/logo.webp";

/**
 * Callback invoked after a successful login.
 *
 * @callback OnLoginSuccess
 * @param {Object} user Newly created/identified user object.
 */

/**
 * @typedef {Object} LoginScreenProps
 * @property {OnLoginSuccess} [onLoginSuccess] Callback fired after a successful login with the created user object.
 */

/**
 * Login screen component.
 * Allows the user to input a unique username and start a game session.
 * Uses i18n for internationalization with automatic language detection.
 *
 * Accessibility: Labels and helper text are bound via htmlFor / aria-describedby.
 *
 * @component
 * @param {LoginScreenProps} props Component props.
 * @returns {JSX.Element} Rendered login screen.
 */
const LoginScreen = ({ onLoginSuccess }) => {
  const { login, onlinePlayers, activeTables = [] } = useGame();
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handles login form submission.
   * Performs a lightweight optimistic flow and simulates a short delay for UX feedback.
   *
   * @param {Event} e Submit event.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // Simular um pequeno delay para melhor UX
    setTimeout(() => {
      const result = login({ username });

      if (result.success) {
        onLoginSuccess?.(result.user);
      } else {
        setError(result.error);
      }

      setIsLoading(false);
    }, 500);
  };

  return (
    <div className="bg-login min-h-screen bg-[var(--color-background)] flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1>
            <picture alt="Guild for Glory Logo">
              <source srcSet={logo} type="image/webp" />
              <img
                src={logo}
                alt="Guild for Glory Logo"
                className="w-80 h-auto sm:w-64 mx-auto"
              />
            </picture>
          </h1>
        </div>

        {/* Login Form */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif">{t("login.title")}</CardTitle>
            <CardDescription>{t("login.description")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">{t("login.username")}</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder={t("login.usernamePlaceholder")}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isLoading}
                  autoComplete="username"
                  autoFocus
                  aria-describedby="username-help"
                />
                <p
                  id="username-help"
                  className="text-sm text-[var(--color-muted-foreground)]"
                >
                  {t("login.usernameHelp")}
                </p>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                variant="primary"
                disabled={!username.trim() || isLoading}
              >
                {isLoading ? t("login.enteringButton") : t("login.enterButton")}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Online Players and Tables Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex flex-col items-center gap-2 font-serif">
              <div className="flex items-center">
                <Users className="w-5 h-5" />
                {t("login.onlinePlayers")}
                <Badge className="ml-1" variant="secondary">
                  {onlinePlayers.length}
                </Badge>
              </div>
              <div className="flex items-center">
                <Table className="w-5 h-5" />
                {t("login.activeTables")}
                <Badge className="ml-1" variant="secondary">
                  {activeTables.length}
                </Badge>
              </div>
            </CardTitle>
          </CardHeader>
        </Card>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-[var(--color-muted-foreground)]">
            {t("login.footer")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
